import { Form, Formik } from "formik";
import React, { useContext, useEffect, useState } from "react";
import { useMessageSource } from "react-message-source";
import { ApplicationFlows } from "../../route/application/authenticated/application-flow/types";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import ContentHeader from "../../ui/layout/page/content/header";
import FormContainer from "../../ui/layout/page/forms-container/form-base-container";
import FlowHeader from "../../ui/widgets/flow-header";
import ProductImage from "../shared/components/product-image";
import { usePageTitle } from "../shared/hooks/usePageTitle";
import CustomSignatories from "./custom";
import PreloadedSignatories from "./preloaded";
import { PersonType, SignatoryModel } from "./types";
import useSigningValidationSchema from "./validator";
import { getCompanyDetail } from "../../api/cybwell";
import { SignatureType } from "../../api/cybwell/types";

import { ErrorContext } from "../../route/error/provider";
import "./styles.scss";
import { LanguageContext } from "../../context/languageContext";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import { ProductRefType } from "../../context/product-context/types";
import { CancelApplicationContext } from "../../context/cancelApplicationContext";
import { ProductContext } from "../../context/product-context/productContext";

const SIGNATORIES_LIST_THRESHOLD = 700;

export type SigningProps = {
  flowType: ApplicationFlows;
  initial: SignatoryModel | null;
  context: {
    selectedProduct: ProductRefType;
    companyName: string;
    cardholder: PersonType;
    ehraid: string;
  };
  next: (data: SignatoryModel) => void;
  previous: (data: SignatoryModel) => void;
};

export default function Signing({
  flowType,
  initial,
  context,
  next,
  previous,
}: SigningProps): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const [persons, setPersons] = useState([]);
  const { setError } = useContext(ErrorContext);
  const { setCancelDialogActive } = useContext(CancelApplicationContext);

  useEffect(() => {
    if (context.ehraid) {
      try {
        getCompanyDetail(context.ehraid).then((res) => {
          setPersons(res.persons);
        });
      } catch (error) {
        setError(error);
      }
    }
  }, [context.ehraid, setError]);

  const hasSignatoriesFromCybwell = persons && persons.length > 0;
  const exceedsSignatoriesThreshold =
    hasSignatoriesFromCybwell && persons.length > SIGNATORIES_LIST_THRESHOLD;

  const { language } = useContext(LanguageContext);
  const { selectedProduct } = useContext(ProductContext);
  return (
    <>
      <FlowHeader
        title={usePageTitle(flowType)}
        image={<ProductImage product={selectedProduct?.productRef} />}
        subTitle={context.companyName}
      />
      <ApplicationPageContent>
        <ContentHeader title={getMessage("Signing.title")} />
        <Formik
          key={language}
          validationSchema={useSigningValidationSchema()}
          onSubmit={(values) => {
            next(values);
          }}
          initialValues={initial}
        >
          {({
            values,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            submitCount,
            errors,
          }) => {
            const setNameAndSignatureType = (
              dataPath: string,
              firstName: string,
              lastName: string,
              signatureType: SignatureType,
            ) => {
              setFieldValue(`${dataPath}firstName`, firstName);
              setFieldValue(`${dataPath}lastName`, lastName);
              setFieldValue(`${dataPath}signatureType`, signatureType);
            };

            return (
              <Form className="scd-section-pusher-lg">
                <FormContainer>
                  {hasSignatoriesFromCybwell && !exceedsSignatoriesThreshold ? (
                    <PreloadedSignatories
                      setNameAndSignatureType={setNameAndSignatureType}
                      persons={persons}
                      values={values}
                      flowType={flowType}
                    />
                  ) : (
                    <CustomSignatories values={values} flowType={flowType} />
                  )}
                </FormContainer>
                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  <NavigationBar
                    onCancel={() => setCancelDialogActive(true)}
                    cancelButtonText={getMessage("Onboarding.cancel")}
                    isSubmitting={isSubmitting}
                    onBack={() => previous(values)}
                    backButtonText={getMessage("Onboarding.back")}
                    onNext={handleSubmit}
                    nextButtonText={getMessage("Onboarding.continue")}
                  />
                </ContentFooter>
              </Form>
            );
          }}
        </Formik>
      </ApplicationPageContent>
    </>
  );
}
